export interface TraceLink {
  kind: string;
  url: string;
}
export interface ComponentVersion {
  component: string;
  status?: string;
  reason?: string;
  releaseId?: string | null;
  buildVersion?: string | null;
  fullSourceSha?: string | null;
  buildId?: number | string | null;
  buildNumber?: string | null;
  buildTimestampUtc?: string | null;
  sourceBranch?: string | null;
  environment?: string | null;
  digest?: string | null;
  artifact?: string | null;
  links?: TraceLink[];
  legacy?: Record<string, unknown>;
}
export interface PluginVersion {
  host: string;
  id: string;
  name?: string | null;
  version?: string | null;
  build?: string | null;
  description?: string | null;
  declared: boolean;
  installed: boolean;
  loaded: boolean;
  active: boolean;
  status: string;
  declaredCapabilities: string[];
  activeCapabilities: string[];
  declaredEvents: string[];
  registeredEvents: string[];
}
export interface PluginHostInventory {
  host: string;
  status: string;
  limitation?: string;
  plugins: PluginVersion[];
}
export interface HistoryEntry {
  id: string;
  label: string;
  value: Record<string, unknown>;
}
export const componentFields: Array<[keyof ComponentVersion, string]> = [
  ['releaseId', 'Full release ID'],
  ['buildVersion', 'Four-part version'],
  ['fullSourceSha', 'Source commit'],
  ['buildId', 'Azure BuildId'],
  ['buildNumber', 'Azure build number'],
  ['buildTimestampUtc', 'Build timestamp'],
  ['sourceBranch', 'Branch'],
  ['environment', 'Environment'],
  ['digest', 'Image digest'],
  ['artifact', 'Artifact identity'],
];
export function formatComponent(component: ComponentVersion): string {
  return [
    `Component: ${component.component}`,
    ...(component.status ? [`Status: ${component.status}`] : []),
    ...(component.reason ? [`Reason: ${component.reason}`] : []),
    ...componentFields.map(([key, label]) => `${label}: ${component[key] ?? 'Unavailable'}`),
    ...(component.links ?? []).map((link) => `${link.kind}: ${link.url}`),
    ...(component.legacy
      ? [`Older version details: ${JSON.stringify(component.legacy, null, 2)}`]
      : []),
  ].join('\n');
}
export function formatPlugin(plugin: PluginVersion): string {
  return [
    `Plugin: ${plugin.host}/${plugin.id}`,
    `Name: ${plugin.name ?? 'Unavailable'}`,
    `Version: ${plugin.version ?? 'Unavailable'}`,
    `Build: ${plugin.build ?? 'Unavailable'}`,
    `Description: ${plugin.description ?? 'Unavailable'}`,
    `Declared: ${plugin.declared}`,
    `Installed: ${plugin.installed}`,
    `Loaded: ${plugin.loaded}`,
    `Active: ${plugin.active}`,
    `Status: ${plugin.status}`,
    `Declared capabilities: ${plugin.declaredCapabilities.join(', ') || 'None declared'}`,
    `Active capabilities: ${plugin.activeCapabilities.join(', ') || 'None active'}`,
    `Declared events: ${plugin.declaredEvents.join(', ') || 'None declared'}`,
    `Registered events: ${plugin.registeredEvents.join(', ') || 'None registered'}`,
  ].join('\n');
}
function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function records(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.filter(record) : [];
}

// Older Identity endpoints do not use the component contract. Identify that known
// service explicitly; never assign an anonymous response by its array position.
export function readComponentVersions(payload: unknown): ComponentVersion[] {
  const components = records(record(payload) ? payload.components : []).flatMap(
    (value): ComponentVersion[] => {
      if (typeof value.component === 'string' && value.component.trim()) {
        return [value as unknown as ComponentVersion];
      }
      if (value.service === 'fusion-identity') {
        return [{ component: 'identity', status: 'legacy', legacy: value }];
      }
      return [];
    },
  );
  for (const component of ['web', 'bff', 'api', 'identity']) {
    if (!components.some((value) => value.component === component)) {
      components.push({ component, status: 'unavailable' });
    }
  }
  const order = ['web', 'bff', 'api', 'identity'];
  return components.sort((left, right) => {
    const rank = (component: string) =>
      order.includes(component) ? order.indexOf(component) : order.length;
    return rank(left.component) - rank(right.component);
  });
}

export function sameComponentBuild(left?: ComponentVersion, right?: ComponentVersion): boolean {
  if (!left?.releaseId || left.releaseId !== right?.releaseId) return false;
  if ([left.status, right.status].some((status) => status === 'unavailable' || status === 'legacy'))
    return false;
  return (['buildVersion', 'fullSourceSha', 'buildId'] as const).every(
    (key) => left[key] == null || right[key] == null || String(left[key]) === String(right[key]),
  );
}
export function historyEntries(history: unknown): HistoryEntry[] {
  if (!record(history)) return [];
  return [
    ...records(history.current).map((value, index) => ({
      id: `current-${index}`,
      label: `Current ${String(value.component ?? `component ${index + 1}`)}`,
      value,
    })),
    ...records(history.publishedReleases).map((value, index) => ({
      id: `publication-${index}`,
      label: `Published release ${String(value.displayTitle ?? value.releaseId ?? index + 1)}`,
      value,
    })),
    ...records(history.retainedObservations).map((value, index) => ({
      id: `observation-${index}`,
      label: `Retained observation ${String(value.version ?? value.buildId ?? index + 1)}`,
      value,
    })),
  ];
}
export function historyGaps(history: unknown): string[] {
  if (history === null || history === undefined) return [];
  if (!record(history)) return ['History is unavailable.'];
  const gaps = Array.isArray(history.historicalGaps)
    ? history.historicalGaps.filter((value): value is string => typeof value === 'string')
    : [];
  if (typeof history.historicalGap === 'string') gaps.push(history.historicalGap);
  if (typeof history.reason === 'string' && history.reason.trim()) gaps.push(history.reason);
  if (history.status === 'unavailable' && gaps.length === 0) gaps.push('History is unavailable.');
  return [...new Set(gaps)];
}
export function formatHistoryEntry(entry: HistoryEntry): string {
  return `${entry.label}\n${JSON.stringify(entry.value, null, 2)}`;
}
export async function copyCompleteText(value: string): Promise<void> {
  if (!navigator.clipboard?.writeText) throw new Error('Clipboard is unavailable');
  await navigator.clipboard.writeText(value);
}
